import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Paper,
  IconButton,
  Divider,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  LocalBar as DrinkIcon,
  Restaurant as FoodIcon,
  MenuBook as CourseIcon,
  SelectAll as SelectAllIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { DrinkFoodPlan, Product, ProductCategory } from '../types';
import ImageUpload from './ImageUpload';

interface PlanDialogProps {
  open: boolean;
  plan: DrinkFoodPlan | null;
  plans: DrinkFoodPlan[];
  onClose: () => void;
  onSuccess: () => void;
}

const PlanDialog: React.FC<PlanDialogProps> = ({ open, plan, plans, onClose, onSuccess }) => {
  const { shop } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameTh, setNameTh] = useState('');
  const [type, setType] = useState<'all_you_can_drink' | 'all_you_can_eat' | 'course'>('all_you_can_drink');
  const [price, setPrice] = useState<number>(0);
  const [timeLimit, setTimeLimit] = useState<number>(120);
  const [lastOrderBefore, setLastOrderBefore] = useState<number>(30);
  const [description, setDescription] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionTh, setDescriptionTh] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [basePlanId, setBasePlanId] = useState<string>('');

  // 延長設定
  const [extensionEnabled, setExtensionEnabled] = useState(false);
  const [extensionTime, setExtensionTime] = useState<number>(30);
  const [extensionPrice, setExtensionPrice] = useState<number>(0);
  const [maxExtensions, setMaxExtensions] = useState<number | ''>('');

  // 商品リスト
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [availableChecked, setAvailableChecked] = useState<string[]>([]);
  const [selectedChecked, setSelectedChecked] = useState<string[]>([]);

  // カテゴリフィルタ
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');

  useEffect(() => {
    if (open && shop) {
      fetchProducts();
    }
  }, [open, shop]);

  useEffect(() => {
    if (plan) {
      setName(plan.name);
      setNameEn(plan.nameEn || '');
      setNameTh(plan.nameTh || '');
      setType(plan.type);
      setPrice(plan.price);
      setTimeLimit(plan.timeLimit);
      setLastOrderBefore(plan.lastOrderBefore);
      setDescription(plan.description || '');
      setDescriptionEn(plan.descriptionEn || '');
      setDescriptionTh(plan.descriptionTh || '');
      setIsActive(plan.isActive);
      setImageUrl((plan as any).imageUrl || '');
      setBasePlanId(plan.basePlanId || '');

      // 延長設定
      if (plan.extensionSettings) {
        setExtensionEnabled(plan.extensionSettings.enabled);
        setExtensionTime(plan.extensionSettings.extensionTime);
        setExtensionPrice(plan.extensionSettings.extensionPrice);
        setMaxExtensions(plan.extensionSettings.maxExtensions ?? '');
      } else {
        setExtensionEnabled(false);
        setExtensionTime(30);
        setExtensionPrice(0);
        setMaxExtensions('');
      }
    } else {
      // 新規作成時のリセット
      setName('');
      setNameEn('');
      setNameTh('');
      setType('all_you_can_drink');
      setPrice(0);
      setTimeLimit(120);
      setLastOrderBefore(30);
      setDescription('');
      setDescriptionEn('');
      setDescriptionTh('');
      setIsActive(true);
      setImageUrl('');
      setBasePlanId('');

      // 延長設定
      setExtensionEnabled(false);
      setExtensionTime(30);
      setExtensionPrice(0);
      setMaxExtensions('');
    }
  }, [plan]);

  // 継承元プランが変更されたら、商品リストを更新
  useEffect(() => {
    updateProductLists();
  }, [basePlanId, allProducts, plan]);

  const fetchProducts = async () => {
    if (!shop) return;

    try {
      // 商品とカテゴリを並列取得
      const [productsSnapshot, categoriesSnapshot] = await Promise.all([
        getDocs(query(
          collection(db, 'products'),
          where('shopId', '==', shop.id),
          where('isActive', '==', true)
        )),
        getDocs(query(
          collection(db, 'productCategories'),
          where('shopId', '==', shop.id),
          where('isActive', '==', true)
        )),
      ]);

      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Product[];

      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as ProductCategory[];

      categoriesData.sort((a, b) => a.sortOrder - b.sortOrder);
      setCategories(categoriesData);
      setAllProducts(productsData);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('商品の取得に失敗しました');
    }
  };

  const updateProductLists = () => {
    if (allProducts.length === 0) return;

    let baseProductIds: string[] = [];

    // 継承元プランの商品IDを取得
    if (basePlanId) {
      const basePlan = plans.find(p => p.id === basePlanId);
      if (basePlan) {
        baseProductIds = basePlan.includedProductIds;
      }
    }

    // 編集中のプランの商品ID（継承元を除く）
    const currentProductIds = plan?.includedProductIds || [];
    const ownProductIds = currentProductIds.filter(id => !baseProductIds.includes(id));

    // 選択済み商品 = 継承元の商品 + 独自の商品
    const selectedIds = [...baseProductIds, ...ownProductIds];
    const selected = allProducts.filter(p => selectedIds.includes(p.id));
    setSelectedProducts(selected);

    // 利用可能な商品 = 全商品 - 選択済み商品
    const available = allProducts.filter(p => !selectedIds.includes(p.id));
    setAvailableProducts(available);
  };

  const handleToggleAvailable = (productId: string) => {
    const currentIndex = availableChecked.indexOf(productId);
    const newChecked = [...availableChecked];

    if (currentIndex === -1) {
      newChecked.push(productId);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setAvailableChecked(newChecked);
  };

  const handleToggleSelected = (productId: string) => {
    // 継承元プランの商品はチェックできない
    if (basePlanId) {
      const basePlan = plans.find(p => p.id === basePlanId);
      if (basePlan && basePlan.includedProductIds.includes(productId)) {
        return;
      }
    }

    const currentIndex = selectedChecked.indexOf(productId);
    const newChecked = [...selectedChecked];

    if (currentIndex === -1) {
      newChecked.push(productId);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setSelectedChecked(newChecked);
  };

  const handleMoveToSelected = () => {
    const productsToMove = availableProducts.filter(p => availableChecked.includes(p.id));
    setSelectedProducts([...selectedProducts, ...productsToMove]);
    setAvailableProducts(availableProducts.filter(p => !availableChecked.includes(p.id)));
    setAvailableChecked([]);
  };

  const handleMoveToAvailable = () => {
    const productsToMove = selectedProducts.filter(p => selectedChecked.includes(p.id));
    setAvailableProducts([...availableProducts, ...productsToMove]);
    setSelectedProducts(selectedProducts.filter(p => !selectedChecked.includes(p.id)));
    setSelectedChecked([]);
  };

  // カテゴリでフィルタされた利用可能商品
  const filteredAvailableProducts = selectedCategoryId === 'all'
    ? availableProducts
    : availableProducts.filter(p => p.categoryId === selectedCategoryId);

  // カテゴリ名を取得
  const getCategoryName = (categoryId: string): string => {
    return categories.find(c => c.id === categoryId)?.name || '未分類';
  };

  // カテゴリ内の全商品を一括選択して移動
  const handleSelectAllInCategory = (categoryId: string) => {
    const productsInCategory = availableProducts.filter(p => p.categoryId === categoryId);
    if (productsInCategory.length === 0) return;
    setSelectedProducts([...selectedProducts, ...productsInCategory]);
    setAvailableProducts(availableProducts.filter(p => p.categoryId !== categoryId));
    setAvailableChecked([]);
  };

  const isInheritedProduct = (productId: string): boolean => {
    if (!basePlanId) return false;
    const basePlan = plans.find(p => p.id === basePlanId);
    return basePlan ? basePlan.includedProductIds.includes(productId) : false;
  };

  const handleSave = async () => {
    if (!shop) return;

    if (!name.trim()) {
      setError('プラン名を入力してください');
      return;
    }

    if (price <= 0) {
      setError('価格を入力してください');
      return;
    }

    if (selectedProducts.length === 0) {
      setError('商品を1つ以上選択してください');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const includedProductIds = selectedProducts.map(p => p.id);

      // displayOrderの計算（新規作成時）
      const displayOrder = plan ? plan.displayOrder : plans.length;

      // 延長設定のデータ作成
      const extensionSettings = extensionEnabled ? {
        enabled: true,
        extensionTime,
        extensionPrice,
        maxExtensions: maxExtensions === '' ? null : maxExtensions,
      } : null;

      const planData = {
        shopId: shop.id,
        name,
        nameEn: nameEn || null,
        nameTh: nameTh || null,
        type,
        imageUrl: imageUrl || null,
        price,
        timeLimit,
        lastOrderBefore,
        description: description || null,
        descriptionEn: descriptionEn || null,
        descriptionTh: descriptionTh || null,
        includedProductIds,
        basePlanId: basePlanId || null,
        displayOrder,
        isActive,
        extensionSettings,
        updatedAt: Timestamp.now(),
      };

      if (plan) {
        // 更新
        await updateDoc(doc(db, 'drinkFoodPlans', plan.id), planData);
      } else {
        // 新規作成
        await addDoc(collection(db, 'drinkFoodPlans'), {
          ...planData,
          createdAt: Timestamp.now(),
        });
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving plan:', err);
      setError('プランの保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getPlanTypeIcon = (planType: DrinkFoodPlan['type']) => {
    switch (planType) {
      case 'all_you_can_drink':
        return <DrinkIcon fontSize="small" />;
      case 'all_you_can_eat':
        return <FoodIcon fontSize="small" />;
      case 'course':
        return <CourseIcon fontSize="small" />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {plan ? 'プラン編集' : 'プラン追加'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* 基本情報 */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>基本情報</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="プラン名（日本語）"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="種類"
              value={type}
              onChange={(e) => setType(e.target.value as any)}
            >
              <MenuItem value="all_you_can_drink">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DrinkIcon fontSize="small" />
                  飲み放題
                </Box>
              </MenuItem>
              <MenuItem value="all_you_can_eat">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FoodIcon fontSize="small" />
                  食べ放題
                </Box>
              </MenuItem>
              <MenuItem value="course">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CourseIcon fontSize="small" />
                  コース
                </Box>
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <ImageUpload
              currentImageUrl={imageUrl || undefined}
              onImageUploaded={(url) => setImageUrl(url)}
              onImageDeleted={() => setImageUrl('')}
              storagePath={`plans/${shop?.id || 'unknown'}`}
              label="プラン画像（お客様のメニュー画面に表示されます）"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="プラン名（English）"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="プラン名（ภาษาไทย）"
              value={nameTh}
              onChange={(e) => setNameTh(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="価格（円/バーツ）"
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              required
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="制限時間（分）"
              value={timeLimit}
              onChange={(e) => setTimeLimit(Number(e.target.value))}
              helperText="例: 120分"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="ラストオーダー（終了前分）"
              value={lastOrderBefore}
              onChange={(e) => setLastOrderBefore(Number(e.target.value))}
              helperText="例: 30分前"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="説明（日本語）"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="説明（English）"
              value={descriptionEn}
              onChange={(e) => setDescriptionEn(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="説明（ภาษาไทย）"
              value={descriptionTh}
              onChange={(e) => setDescriptionTh(e.target.value)}
            />
          </Grid>

          {/* 継承設定 */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>継承設定</Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="継承元プラン"
              value={basePlanId}
              onChange={(e) => setBasePlanId(e.target.value)}
              helperText="他のプランを継承すると、そのプランの商品が自動的に含まれます"
            >
              <MenuItem value="">なし</MenuItem>
              {plans
                .filter(p => p.id !== plan?.id) // 自分自身は除外
                .map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getPlanTypeIcon(p.type)}
                      {p.name} （{p.includedProductIds.length}品）
                    </Box>
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          {/* 延長設定 */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>延長設定</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              スタッフのみが延長注文できる機能です。お客様側からは延長できません。
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={extensionEnabled}
                  onChange={(e) => setExtensionEnabled(e.target.checked)}
                />
              }
              label="延長機能を有効にする"
            />
          </Grid>

          {extensionEnabled && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="延長時間（分）"
                  value={extensionTime}
                  onChange={(e) => setExtensionTime(Number(e.target.value))}
                  required
                  helperText="例: 30分"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="延長料金（円/バーツ）"
                  value={extensionPrice}
                  onChange={(e) => setExtensionPrice(Number(e.target.value))}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="最大延長回数"
                  value={maxExtensions}
                  onChange={(e) => setMaxExtensions(e.target.value === '' ? '' : Number(e.target.value))}
                  helperText="空白 = 無制限"
                />
              </Grid>
            </>
          )}

          {/* 商品選択 */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>商品選択</Typography>
          </Grid>

          <Grid item xs={12}>
            {/* カテゴリフィルタ */}
            {categories.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  カテゴリ:
                </Typography>
                <Chip
                  label="すべて"
                  size="small"
                  color={selectedCategoryId === 'all' ? 'primary' : 'default'}
                  variant={selectedCategoryId === 'all' ? 'filled' : 'outlined'}
                  onClick={() => setSelectedCategoryId('all')}
                />
                {categories.map((cat) => {
                  const availableCount = availableProducts.filter(p => p.categoryId === cat.id).length;
                  return (
                    <Chip
                      key={cat.id}
                      label={`${cat.name} (${availableCount})`}
                      size="small"
                      color={selectedCategoryId === cat.id ? 'primary' : 'default'}
                      variant={selectedCategoryId === cat.id ? 'filled' : 'outlined'}
                      onClick={() => setSelectedCategoryId(cat.id)}
                    />
                  );
                })}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* 利用可能な商品 */}
              <Paper sx={{ flex: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1">
                    利用可能な商品 ({filteredAvailableProducts.length})
                  </Typography>
                  {selectedCategoryId !== 'all' && filteredAvailableProducts.length > 0 && (
                    <Tooltip title={`${getCategoryName(selectedCategoryId)}を一括追加`}>
                      <Button
                        size="small"
                        startIcon={<SelectAllIcon />}
                        onClick={() => handleSelectAllInCategory(selectedCategoryId)}
                      >
                        一括追加
                      </Button>
                    </Tooltip>
                  )}
                </Box>
                <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'background.paper' }}>
                  {filteredAvailableProducts.map((product) => (
                    <ListItem key={product.id} dense disablePadding>
                      <ListItemButton onClick={() => handleToggleAvailable(product.id)}>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={availableChecked.indexOf(product.id) !== -1}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={product.name}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <span>¥{product.price.toLocaleString()}</span>
                              <Chip component="span" label={getCategoryName(product.categoryId)} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {filteredAvailableProducts.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="商品がありません"
                        sx={{ textAlign: 'center', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>

              {/* 移動ボタン */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <IconButton
                  onClick={handleMoveToSelected}
                  disabled={availableChecked.length === 0}
                  color="primary"
                >
                  <ArrowForwardIcon />
                </IconButton>
                <IconButton
                  onClick={handleMoveToAvailable}
                  disabled={selectedChecked.length === 0}
                  color="primary"
                >
                  <ArrowBackIcon />
                </IconButton>
              </Box>

              {/* 選択済み商品 */}
              <Paper sx={{ flex: 1, p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  プランに含まれる商品 ({selectedProducts.length})
                </Typography>
                <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'background.paper' }}>
                  {selectedProducts.map((product) => {
                    const inherited = isInheritedProduct(product.id);
                    return (
                      <ListItem
                        key={product.id}
                        dense
                        disablePadding
                        sx={inherited ? { bgcolor: 'action.hover' } : {}}
                      >
                        <ListItemButton
                          onClick={() => !inherited && handleToggleSelected(product.id)}
                          disabled={inherited}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={selectedChecked.indexOf(product.id) !== -1}
                              tabIndex={-1}
                              disableRipple
                              disabled={inherited}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {product.name}
                                {inherited && (
                                  <Typography variant="caption" color="primary">
                                    (継承)
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <span>¥{product.price.toLocaleString()}</span>
                                <Chip component="span" label={getCategoryName(product.categoryId)} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  {selectedProducts.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="商品を選択してください"
                        sx={{ textAlign: 'center', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="有効"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanDialog;
